import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Category } from "../types";
import { dataService } from "../services/dataService";
import PlayfulLoader from "../components/PlayfulLoader";

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoading(true);
        const response = await dataService.getCategories();
        if (response.success) {
          setCategories(response.data!);
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const categoryColors = [
    "bg-red-400",
    "bg-green-400",
    "bg-blue-400",
    "bg-pink-400",
    "bg-purple-400",
    "bg-orange-400",
    "bg-yellow-400",
    "bg-indigo-400",
    "bg-teal-400",
    "bg-cyan-400",
  ];

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="rainbow"
        message="🌈 Loading fun categories..."
        fullScreen
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-warm-cream-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-soft-purple-200 via-soft-pink-100 to-soft-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <span className="text-6xl mr-3">🌈</span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              All Categories
            </h1>
          </div>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Explore our complete collection of toy categories and find the
            perfect toys for every age and interest!
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
          {categories.map((category, index) => (
            <Link
              key={category.id}
              to={`/category/${category.id}`}
              className="group cursor-pointer transform hover:scale-105 transition-all duration-300"
            >
              <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100">
                {/* Category Icon */}
                <div
                  className={`${
                    categoryColors[index % categoryColors.length]
                  } p-8 text-center`}
                >
                  <div className="text-5xl mb-2">{category.icon}</div>
                </div>

                {/* Category Info */}
                <div className="p-5 text-center">
                  <h3 className="font-semibold text-gray-900 mb-2 transition-colors duration-200 group-hover:text-purple-600 text-lg">
                    {category.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {category.productCount}{" "}
                    {category.productCount === 1 ? "item" : "items"}
                  </p>
                  {category.description && (
                    <div className="text-xs text-gray-400 line-clamp-2">
                      {category.description}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {categories.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No Categories Available
            </h2>
            <p className="text-gray-600">Check back soon for new categories!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
